/**
 * GraphQL error types - shared between backend and frontend
 * @module validation/graphql-errors
 */

/**
 * Standard GraphQL error codes
 */
export const GraphQLErrorCode = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  // Raised when the operation belongs to a feature that is switched off for the caller
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  // Raised when a request body or uploaded file exceeds the configured size limit
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  // Auth / invitation conflict codes
  EMAIL_CONFLICT_SAME_ORG: 'EMAIL_CONFLICT_SAME_ORG',
  EMAIL_CONFLICT_OTHER_ORG: 'EMAIL_CONFLICT_OTHER_ORG',
  INVITATION_ALREADY_SENT: 'INVITATION_ALREADY_SENT',
  // Raised when a master account invitation can no longer be redeemed or resent: the master account is not active,
  // its inviter no longer administers the master account, or it was cancelled or accepted meanwhile
  INVITATION_NO_LONGER_VALID: 'INVITATION_NO_LONGER_VALID',
  // Raised when an operation would leave the organization without an active admin
  AT_LEAST_ONE_ADMIN: 'AT_LEAST_ONE_ADMIN',
  // Raised when an operation would leave a master account without an active MASTER_ADMIN
  LAST_MASTER_ADMIN: 'LAST_MASTER_ADMIN',
  // Raised when the account is scheduled for deletion and only the reactivation flow is allowed
  ACCOUNT_PENDING_DELETION: 'ACCOUNT_PENDING_DELETION',
  // Raised when the organization name is already used by another account (case-insensitive)
  ACCOUNT_NAME_TAKEN: 'ACCOUNT_NAME_TAKEN',
  // Raised (400) when a write would leave the organization (main party) without an accounting currency
  ORGANIZATION_ACCOUNTING_CURRENCY_REQUIRED: 'ORGANIZATION_ACCOUNTING_CURRENCY_REQUIRED',
  // Raised when a client leaving its master account would take a name an account without a master account already
  // uses (case-insensitive); the organization must be renamed first
  DETACH_ACCOUNT_NAME_TAKEN: 'DETACH_ACCOUNT_NAME_TAKEN',
  // Raised on sign-in when the identity holds no active account membership
  NO_ACTIVE_MEMBERSHIP: 'NO_ACTIVE_MEMBERSHIP',
  // Raised on OAuth sign-in when the provider does not vouch for the profile email
  OAUTH_EMAIL_NOT_VERIFIED: 'OAUTH_EMAIL_NOT_VERIFIED',
  // Raised (401) when a Keycloak identity may not enter AMS; one code for every reason
  AMS_ACCESS_DENIED: 'AMS_ACCESS_DENIED',
  // Raised (400) when an e-mail unsubscribe link is malformed, tampered or signed by an unknown key
  INVALID_UNSUBSCRIBE_LINK: 'INVALID_UNSUBSCRIBE_LINK',
  // OAuth redirect `?error=` code when the user cancelled sign-in at the provider
  AUTH_CANCELLED: 'AUTH_CANCELLED',
  // OAuth redirect `?error=` code for any other failed sign-in
  AUTH_FAILED: 'AUTH_FAILED',
  // Raised when Keycloak SSO is not configured; mapped to a message by the AMS login page
  KEYCLOAK_NOT_CONFIGURED: 'KEYCLOAK_NOT_CONFIGURED',
  // Raised when a dependency the request needs is briefly unavailable; nothing was changed
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  // Raised when the caller exceeded a rate limit; `extensions.retryAfter` may give the seconds
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  // Raised when an entity link decision could not be written to the activity log; nothing changed
  ENTITY_LINK_AUDIT_FAILED: 'ENTITY_LINK_AUDIT_FAILED',
  // Raised when a master account member entitled to a client already holds a direct membership there
  MASTER_ACCOUNT_MEMBERSHIP_CONFLICT: 'MASTER_ACCOUNT_MEMBERSHIP_CONFLICT',
  // Raised when a client tries to edit or remove a membership the master account materialized
  MASTER_ACCOUNT_MEMBERSHIP_MANAGED_BY_MASTER_ACCOUNT:
    'MASTER_ACCOUNT_MEMBERSHIP_MANAGED_BY_MASTER_ACCOUNT',
  // Raised when declaring a master account on an account that already has a master account profile
  ALREADY_A_MASTER_ACCOUNT: 'ALREADY_A_MASTER_ACCOUNT',
  // Raised when an operation requires a master account to have no connected clients first
  MASTER_ACCOUNT_HAS_CLIENTS: 'MASTER_ACCOUNT_HAS_CLIENTS',
  // Raised when inviting a founder to a master account that already has members
  MASTER_ACCOUNT_HAS_MEMBERS: 'MASTER_ACCOUNT_HAS_MEMBERS',
  // Raised when an operation is refused because the account is (or would become) a client
  // managed by a master account
  ACCOUNT_IS_MANAGED_CLIENT: 'ACCOUNT_IS_MANAGED_CLIENT',
  // Raised when a master account/client relationship would nest a master account under another master account
  MASTER_ACCOUNT_DOES_NOT_NEST: 'MASTER_ACCOUNT_DOES_NOT_NEST',
  // Raised when a master account's plan does not allow another managed client
  ACCOUNT_RELATION_LIMIT_REACHED: 'ACCOUNT_RELATION_LIMIT_REACHED',
  // Raised when revoking a master account whose own books ("Moje kancelář") still hold data or people
  OWN_BOOKS_NOT_EMPTY: 'OWN_BOOKS_NOT_EMPTY',
  // Raised when the master account's own books would be disconnected, left or transferred to another master account
  OWN_BOOKS_CANNOT_LEAVE: 'OWN_BOOKS_CANNOT_LEAVE',
  // Raised when a deletion is requested, cancelled or purged from inside the master account's own books
  OWN_BOOKS_FOLLOWS_MASTER_ACCOUNT: 'OWN_BOOKS_FOLLOWS_MASTER_ACCOUNT',
  // Raised when a master-account-side admin requests deletion of a client the master account did not create; only
  // the client's own direct admin may delete an organization that joined the master account
  CLIENT_DELETION_NEEDS_DIRECT_ADMIN: 'CLIENT_DELETION_NEEDS_DIRECT_ADMIN',
  // Raised when a master account disconnects a client with no active user of its own; the master account deletes it
  // instead (or asks support, for a client it did not found)
  ACCOUNT_RELATION_HAS_NO_OWN_USERS: 'ACCOUNT_RELATION_HAS_NO_OWN_USERS',
  // Raised when a master account deletes a client that has active users of its own; it disconnects it instead
  ACCOUNT_RELATION_HAS_OWN_USERS: 'ACCOUNT_RELATION_HAS_OWN_USERS',
  // Raised when a master account deletes a client it did not found (transferred, converted); only support may
  ACCOUNT_RELATION_DELETION_NEEDS_SUPPORT: 'ACCOUNT_RELATION_DELETION_NEEDS_SUPPORT',
  // Raised when a master account declaration does not classify every other member exactly once
  MASTER_ACCOUNT_MEMBER_CLASSIFICATION_INVALID: 'MASTER_ACCOUNT_MEMBER_CLASSIFICATION_INVALID',
  // Raised when the master account's identity (name, IČO, DIČ, address, contact) is edited inside its own
  // books; it is edited in the master account board only
  OWN_BOOKS_IDENTITY_READ_ONLY: 'OWN_BOOKS_IDENTITY_READ_ONLY',
  // Raised when the account's members changed while it was being declared a master account; retry
  MASTER_ACCOUNT_DECLARE_CONFLICT: 'MASTER_ACCOUNT_DECLARE_CONFLICT',
  // Raised when the master account account is deactivated or scheduled for deletion, so it gains no clients
  MASTER_ACCOUNT_ACCOUNT_NOT_ACTIVE: 'MASTER_ACCOUNT_ACCOUNT_NOT_ACTIVE',
  // Raised when a client is created in or transferred to a master account scheduled for deletion
  MASTER_ACCOUNT_PENDING_DELETION: 'MASTER_ACCOUNT_PENDING_DELETION',
  // Raised when the account to be connected as a client is deactivated or scheduled for deletion
  CLIENT_ACCOUNT_NOT_ACTIVE: 'CLIENT_ACCOUNT_NOT_ACTIVE',
  // Raised when assigning a client to someone who does not hold MASTER_ACCOUNTANT
  // in the calling master account
  USER_NOT_A_MASTER_ACCOUNT_ACCOUNTANT: 'USER_NOT_A_MASTER_ACCOUNT_ACCOUNTANT',
  // Raised when creating an assignment that already has a live (unrevoked) counterpart
  ACCOUNT_RELATION_ASSIGNMENT_ALREADY_ACTIVE: 'ACCOUNT_RELATION_ASSIGNMENT_ALREADY_ACTIVE',
  // Raised when a master-account-side write is refused because the paying master account's subscription is locked
  SUBSCRIPTION_LOCKED_READ_ONLY: 'SUBSCRIPTION_LOCKED_READ_ONLY',
  // Raised when document export is suspended by the payer's subscription state
  EXPORT_SUSPENDED: 'EXPORT_SUSPENDED',
  // Raised when starting or enabling an integration is suspended by the payer's subscription state
  INTEGRATIONS_SUSPENDED: 'INTEGRATIONS_SUSPENDED',
  // Raised when an operation needs a master account context and the session holds none
  MASTER_ACCOUNT_CONTEXT_REQUIRED: 'MASTER_ACCOUNT_CONTEXT_REQUIRED',
  // Raised when a signed-in request needs an account context and holds none (it acts in a
  // master account); a 403, never a sign-out
  ACCOUNT_CONTEXT_REQUIRED: 'ACCOUNT_CONTEXT_REQUIRED',
  // Raised (403) when the office client header cannot put the request into that client: wrong
  // session or caller, not an Account Node ID, or a client the caller does not see
  OFFICE_CLIENT_CONTEXT_REFUSED: 'OFFICE_CLIENT_CONTEXT_REFUSED',
  // Raised (403) when an operation is not offered through the office client header
  OFFICE_CLIENT_SCOPE_REQUIRED: 'OFFICE_CLIENT_SCOPE_REQUIRED',
  // Raised (403) when master-context work cannot act in that client; one code for every reason
  CLIENT_NOT_AVAILABLE: 'CLIENT_NOT_AVAILABLE',
  // Raised when a question to the client needs a reader, but the client has no active own user
  CLIENT_HAS_NO_DIRECT_USER: 'CLIENT_HAS_NO_DIRECT_USER',
  // Raised when opening a thread on a document that already has an open one
  DOCUMENT_THREAD_ALREADY_OPEN: 'DOCUMENT_THREAD_ALREADY_OPEN',
  // Raised when posting to a thread that has been resolved
  DOCUMENT_THREAD_RESOLVED: 'DOCUMENT_THREAD_RESOLVED',
  // `extensions.errorCode` of the SERVICE_UNAVAILABLE raised when the billing portal cannot open
  BILLING_PORTAL_UNAVAILABLE: 'BILLING_PORTAL_UNAVAILABLE',
  // `extensions.errorCode` of the SERVICE_UNAVAILABLE raised when the database cannot serve a request
  DATABASE_UNAVAILABLE: 'DATABASE_UNAVAILABLE',
  // Raised (400) when an invoice is handed over for approval while its validation waits for credits
  INVOICE_VALIDATION_WAITING_FOR_CREDITS: 'INVOICE_VALIDATION_WAITING_FOR_CREDITS',
  // Raised when a document e-mail could not be sent; the document is unchanged
  DOCUMENT_EMAIL_NOT_SENT: 'DOCUMENT_EMAIL_NOT_SENT',
  // Raised when a document e-mail went out but the document's status could not follow
  DOCUMENT_EMAIL_SENT_STATUS_NOT_UPDATED: 'DOCUMENT_EMAIL_SENT_STATUS_NOT_UPDATED',
  // Raised when the same document is already being sent by another request
  DOCUMENT_SEND_IN_PROGRESS: 'DOCUMENT_SEND_IN_PROGRESS',
  // Raised (409) when deleting a Bridge that an installation still names
  BRIDGE_IN_USE: 'BRIDGE_IN_USE',
  // Raised (409) when deleting a master account that still owns a Bridge
  MASTER_ACCOUNT_OWNS_BRIDGE: 'MASTER_ACCOUNT_OWNS_BRIDGE',
  // Raised (403) when someone outside the office configures a master account's Bridge
  BRIDGE_MANAGED_BY_MASTER_ACCOUNT: 'BRIDGE_MANAGED_BY_MASTER_ACCOUNT',
  // Raised (409) when another client already holds the agenda on a master account's Bridge
  BRIDGE_AGENDA_TAKEN: 'BRIDGE_AGENDA_TAKEN',
  // Raised (400) when an installation on a master account's Bridge has no complete agenda key
  BRIDGE_AGENDA_INCOMPLETE: 'BRIDGE_AGENDA_INCOMPLETE',
  // Raised (403) when the own books' Bridge hand-over is asked outside the own books or by non-staff
  BRIDGE_TRANSFER_NOT_ALLOWED: 'BRIDGE_TRANSFER_NOT_ALLOWED',
  // Raised (400) when an integration template's configuration migration reported issues
  INTEGRATION_TEMPLATE_NEEDS_REVIEW: 'INTEGRATION_TEMPLATE_NEEDS_REVIEW',
  // Raised (400) when a plugin has no installation to template: no connection scope, or a secret
  // kept on the connector
  INTEGRATION_TEMPLATE_PLUGIN_UNSUPPORTED: 'INTEGRATION_TEMPLATE_PLUGIN_UNSUPPORTED',
  // Raised when a client already has a connector of the template's plugin and entity
  INTEGRATION_TEMPLATE_CLIENT_ALREADY_CONFIGURED: 'INTEGRATION_TEMPLATE_CLIENT_ALREADY_CONFIGURED',
  // Raised (400) when a template holds a value or a secret for a key supplied per client
  INTEGRATION_TEMPLATE_PER_CLIENT_KEY_SET: 'INTEGRATION_TEMPLATE_PER_CLIENT_KEY_SET',
  // Raised (400) when a template's keys or connectors do not fit its plugin
  INTEGRATION_TEMPLATE_CONFIG_INVALID: 'INTEGRATION_TEMPLATE_CONFIG_INVALID',
  // Raised (409) when the master account already has a template of that name
  INTEGRATION_TEMPLATE_NAME_CONFLICT: 'INTEGRATION_TEMPLATE_NAME_CONFLICT',
  // Raised (409) when another application of a template runs in the same master account
  INTEGRATION_TEMPLATE_APPLY_IN_PROGRESS: 'INTEGRATION_TEMPLATE_APPLY_IN_PROGRESS',
  // Raised (409) when a held batch of missing records changed or was settled since it was read
  MISSING_HOLD_CHANGED: 'MISSING_HOLD_CHANGED',
  // Raised (409) when a held batch is confirmed while its connector is switched off
  MISSING_HOLD_CONNECTOR_OFF: 'MISSING_HOLD_CONNECTOR_OFF',
  // Raised (409) when a confirmed held batch is confirmed again with nothing left to finish
  MISSING_HOLD_NOTHING_TO_FINISH: 'MISSING_HOLD_NOTHING_TO_FINISH',
  // Raised (409) when a payer returned to documents switches back to credits before that credit
  // period ends; `extensions.periodEnd` is when it may
  CREDITS_RETURNED_THIS_PERIOD: 'CREDITS_RETURNED_THIS_PERIOD',
  // Raised (409) when another plan switch of the same payer committed first; reload and retry
  PAYER_PLAN_CHANGED_MEANWHILE: 'PAYER_PLAN_CHANGED_MEANWHILE',
  // Raised (409) when an operator grants or raises credits of a payer not on a CREDITS plan
  PAYER_NOT_ON_CREDIT_PLAN: 'PAYER_NOT_ON_CREDIT_PLAN',
  // Raised when a credit pack is not offered to the payer (its plan or its currency)
  CREDIT_PACK_NOT_OFFERED: 'CREDIT_PACK_NOT_OFFERED',
  // Raised when the payer may not buy a credit pack now; `extensions.reason` is a
  // `CreditPackPurchaseRefusal`
  CREDIT_PACK_PURCHASE_REFUSED: 'CREDIT_PACK_PURCHASE_REFUSED',
  // Raised (400) when a bank account fails its scheme's check; `extensions.bankingErrors` lists why
  BANK_ACCOUNT_INVALID: 'BANK_ACCOUNT_INVALID',
  // Raised (409) when the party already has an active bank account with the same identity
  BANK_ACCOUNT_DUPLICATE: 'BANK_ACCOUNT_DUPLICATE',
  // Raised (409) when a deactivated bank account is edited, signed, rejected or revoked
  BANK_ACCOUNT_INACTIVE: 'BANK_ACCOUNT_INACTIVE',
  // Raised (409) when the bank account's verification state does not allow the action
  BANK_ACCOUNT_STATE_CONFLICT: 'BANK_ACCOUNT_STATE_CONFLICT',
  // Raised (403) when the person who created the bank account gives its second signature
  BANK_ACCOUNT_SIGNER_IS_CREATOR: 'BANK_ACCOUNT_SIGNER_IS_CREATOR',
  // Raised (403) when the first signer of the bank account gives its second signature too
  BANK_ACCOUNT_SIGNER_IS_FIRST_SIGNER: 'BANK_ACCOUNT_SIGNER_IS_FIRST_SIGNER',
  // Raised (403) when the person who last changed the bank account gives its second signature
  BANK_ACCOUNT_SIGNER_IS_LAST_EDITOR: 'BANK_ACCOUNT_SIGNER_IS_LAST_EDITOR',
  // Raised (403) when a bank account is changed without a person in the request
  BANK_ACCOUNT_PERSON_REQUIRED: 'BANK_ACCOUNT_PERSON_REQUIRED',
  // Raised (409) when a signature expected a verification state the account has left meanwhile
  BANK_ACCOUNT_STATE_CHANGED: 'BANK_ACCOUNT_STATE_CHANGED',
  // Raised (400) when an invoice prints no valid account, or two different ones, to accept
  BANK_ACCOUNT_NOT_PRINTED: 'BANK_ACCOUNT_NOT_PRINTED',
  // Raised (409) at the second signature when the evidence of the system's first signature no
  // longer stands: the party's VAT ID or IČO changed (E3), the statement confirmation ended (E2)
  BANK_ACCOUNT_EVIDENCE_STALE: 'BANK_ACCOUNT_EVIDENCE_STALE',
  // Raised (403) at the second signature over the system's E2 by whoever uploaded the statement
  // whose confirmation gave it
  BANK_ACCOUNT_SIGNER_IS_STATEMENT_UPLOADER: 'BANK_ACCOUNT_SIGNER_IS_STATEMENT_UPLOADER',
  // Raised (400) when an invoice's account is accepted but the invoice names no supplier party
  INVOICE_SUPPLIER_NOT_LINKED: 'INVOICE_SUPPLIER_NOT_LINKED',
  // Raised (403) when a confirmation, its revocation or a recorded payment is not a person's own
  // act: an API key, a machine identity, the system user or impersonation
  PAYMENT_PERSON_REQUIRED: 'PAYMENT_PERSON_REQUIRED',
  // Raised (403) when whoever built or edited a payment's run confirms it by hand
  PAYMENT_CONFIRMER_IS_RUN_AUTHOR: 'PAYMENT_CONFIRMER_IS_RUN_AUTHOR',
  // Raised (409) when a person's confirmation of the payment already stands
  PAYMENT_ALREADY_CONFIRMED: 'PAYMENT_ALREADY_CONFIRMED',
  // Raised (409) when the payment is not in a state a person may confirm
  PAYMENT_NOT_CONFIRMABLE: 'PAYMENT_NOT_CONFIRMABLE',
  // Raised (409) when the confirmation is not a standing confirmation by a person or a statement line
  PAYMENT_CONFIRMATION_NOT_REVOCABLE: 'PAYMENT_CONFIRMATION_NOT_REVOCABLE',
  // Raised (409) when a return is revoked while an invoice of the payment is paid by another live
  // payment: the payment would pay it twice
  PAYMENT_RETURN_PAID_AGAIN: 'PAYMENT_RETURN_PAID_AGAIN',
  // Raised (409) when the confirmation of a partial split is revoked: a split is never undone
  PAYMENT_PARTIAL_NOT_REVOCABLE: 'PAYMENT_PARTIAL_NOT_REVOCABLE',
  // Raised (409) when a statement line's confirmation is revoked while a run whose payment the line
  // confirmed before was withdrawn after that revocation: the line's evidence was used
  PAYMENT_CONFIRMATION_EVIDENCE_USED: 'PAYMENT_CONFIRMATION_EVIDENCE_USED',
  // Raised (400) when a confirmation is revoked without a reason
  PAYMENT_REVOCATION_REASON_REQUIRED: 'PAYMENT_REVOCATION_REASON_REQUIRED',
  // Raised (400) when a payment date lies in the future
  PAYMENT_DATE_IN_FUTURE: 'PAYMENT_DATE_IN_FUTURE',
  // Raised (409) when the invoice takes no recorded payment: issued, cancelled, a credit note, or
  // without a total or a currency
  PAYMENT_INVOICE_NOT_RECORDABLE: 'PAYMENT_INVOICE_NOT_RECORDABLE',
  // Raised (409) when a payment is recorded by hand on an invoice that is not approved
  PAYMENT_INVOICE_NOT_APPROVED: 'PAYMENT_INVOICE_NOT_APPROVED',
  // Raised (409) when a payment from the queue names an invoice that can still be deleted (a draft
  // or a refusal): it is submitted first
  PAYMENT_INVOICE_NOT_SUBMITTED: 'PAYMENT_INVOICE_NOT_SUBMITTED',
  // Raised (409) when a recorded payment would settle more than the invoice still owes
  PAYMENT_EXCEEDS_OUTSTANDING: 'PAYMENT_EXCEEDS_OUTSTANDING',
  // Raised (400) when an amount is not above zero, or a discount or withholding is below zero
  PAYMENT_AMOUNT_INVALID: 'PAYMENT_AMOUNT_INVALID',
  // Raised (400) when a discount is taken by a payment that does not settle the invoice in full
  PAYMENT_DISCOUNT_NOT_FULL_SETTLEMENT: 'PAYMENT_DISCOUNT_NOT_FULL_SETTLEMENT',
  // Raised (400) when the payee is not the invoice's supplier party or a person of the account
  PAYMENT_PAYEE_INVALID: 'PAYMENT_PAYEE_INVALID',
  // Raised (409) when the account credited is not a verified, active account of the payee
  PAYMENT_CREDIT_ACCOUNT_NOT_VERIFIED: 'PAYMENT_CREDIT_ACCOUNT_NOT_VERIFIED',
  // Raised (409) when an invoice in the payment ledger is deleted
  INVOICE_IN_PAYMENT_LEDGER: 'INVOICE_IN_PAYMENT_LEDGER',
  // Raised (409) when a run's debit account is not an active, verified account of our company
  PAYMENT_DEBIT_ACCOUNT_INVALID: 'PAYMENT_DEBIT_ACCOUNT_INVALID',
  // Raised (400) when a run would mix currencies, or its format does not fit its currency
  PAYMENT_RUN_NOT_HOMOGENEOUS: 'PAYMENT_RUN_NOT_HOMOGENEOUS',
  // Raised (409) when a proposal's selection finds no invoice that can be paid now
  PAYMENT_RUN_NOTHING_TO_PAY: 'PAYMENT_RUN_NOTHING_TO_PAY',
  // Raised (409) when the run's state does not allow the change (edited from RELEASED on)
  PAYMENT_RUN_NOT_EDITABLE: 'PAYMENT_RUN_NOT_EDITABLE',
  // Raised (409) when a run with no payment left is submitted for approval
  PAYMENT_RUN_NO_PAYMENT: 'PAYMENT_RUN_NO_PAYMENT',
  // Raised (409) when an edit would leave a payment's transfer at zero or below
  PAYMENT_TRANSFER_NOT_POSITIVE: 'PAYMENT_TRANSFER_NOT_POSITIVE',
  // Raised (409) when a credit note cannot be netted: not the payee's, another currency, not
  // approved, cancelled, or less of it left than asked
  PAYMENT_CREDIT_NOTE_NOT_NETTABLE: 'PAYMENT_CREDIT_NOTE_NOT_NETTABLE',
  // Raised (403) when whoever built or edited a payment run approves it, or unblocks a payment of
  // it after approval
  PAYMENT_RUN_APPROVER_IS_AUTHOR: 'PAYMENT_RUN_APPROVER_IS_AUTHOR',
  // Raised (403) when a run is released by whoever built, edited or approved it (an unblock after
  // approval included), or a payment is unblocked after release by whoever released it
  PAYMENT_RUN_RELEASER_NOT_THIRD_PERSON: 'PAYMENT_RUN_RELEASER_NOT_THIRD_PERSON',
  // Raised (409) when the release re-check finds an invoice, a credit note or an account no longer
  // payable; extensions.releaseFailures names each
  PAYMENT_RUN_RELEASE_CHECK_FAILED: 'PAYMENT_RUN_RELEASE_CHECK_FAILED',
  // Raised (409) when a payment is blocked or unblocked in a state that does not allow it
  PAYMENT_NOT_BLOCKABLE: 'PAYMENT_NOT_BLOCKABLE',
  // Raised (400) when a payment is blocked without a reason
  PAYMENT_BLOCK_REASON_REQUIRED: 'PAYMENT_BLOCK_REASON_REQUIRED',
  // Raised (409) when a released run is cancelled, or an exported run withdrawn, while evidence
  // that a payment moved stands
  PAYMENT_RUN_HAS_CONFIRMATIONS: 'PAYMENT_RUN_HAS_CONFIRMATIONS',
  // Raised (409) when an exported run is withdrawn while a statement line whose confirmation of
  // one of its payments was revoked has confirmed no other payment since
  PAYMENT_RUN_HAS_BANK_LINE_EVIDENCE: 'PAYMENT_RUN_HAS_BANK_LINE_EVIDENCE',
  // Raised (409) when the export re-check finds an account, a confirmation or the bank file
  // refusing the run; extensions.exportFailures names each
  PAYMENT_RUN_EXPORT_CHECK_FAILED: 'PAYMENT_RUN_EXPORT_CHECK_FAILED',
  // Raised (400) when an export is withdrawn without a reason
  PAYMENT_WITHDRAWAL_REASON_REQUIRED: 'PAYMENT_WITHDRAWAL_REASON_REQUIRED',
  // Raised (409) when a payment other than one left out of an exported run's file is cancelled
  // as such
  PAYMENT_NOT_CANCELLABLE: 'PAYMENT_NOT_CANCELLABLE',
  // Raised (409) when splitting a partly confirmed payment would leave a side at zero or below
  // because of a netted credit note
  PAYMENT_SPLIT_CREDIT_NOTE: 'PAYMENT_SPLIT_CREDIT_NOTE',
  // Raised (403) when a statement is uploaded by impersonation, an AMS operator, the system user or
  // a service token; a person or an API key whose user holds the permission may upload
  BANK_STATEMENT_UPLOADER_INVALID: 'BANK_STATEMENT_UPLOADER_INVALID',
  // Raised (400) when a statement upload carries no file
  BANK_STATEMENT_FILE_MISSING: 'BANK_STATEMENT_FILE_MISSING',
  // Raised (400) when a statement's file name is empty, unsafe or longer than 255 characters
  BANK_STATEMENT_FILE_NAME_INVALID: 'BANK_STATEMENT_FILE_NAME_INVALID',
  // Raised (400) when a statement upload declares a format that is not a statement format
  BANK_STATEMENT_FORMAT_INVALID: 'BANK_STATEMENT_FORMAT_INVALID',
  // Raised (409) when a statement line's proposal was decided meanwhile, or no longer stands when
  // matched again (it is then superseded)
  BANK_MOVEMENT_PROPOSAL_STALE: 'BANK_MOVEMENT_PROPOSAL_STALE',
  // Raised (403) when whoever built or edited a run accepts or rejects a proposal for its payment
  BANK_MOVEMENT_PROPOSAL_DECIDER_IS_RUN_AUTHOR: 'BANK_MOVEMENT_PROPOSAL_DECIDER_IS_RUN_AUTHOR',
  // Raised (403) when whoever uploaded the line's statement, or anyone while its uploader is
  // unknown, accepts or rejects one of its proposals
  BANK_MOVEMENT_PROPOSAL_DECIDER_IS_UPLOADER: 'BANK_MOVEMENT_PROPOSAL_DECIDER_IS_UPLOADER',
  // Raised (400) when a proposal is rejected without a note, or a short return accepted without one
  BANK_MOVEMENT_PROPOSAL_NOTE_REQUIRED: 'BANK_MOVEMENT_PROPOSAL_NOTE_REQUIRED',
  // Raised (409) when a proposal whose counterparty is not the payment's account is accepted
  BANK_MOVEMENT_PROPOSAL_COUNTERPARTY_DIFFERS: 'BANK_MOVEMENT_PROPOSAL_COUNTERPARTY_DIFFERS',
  // Raised (409) when a proposal naming DUPLICATE (a statement already confirmed the payment) is
  // accepted
  BANK_MOVEMENT_PROPOSAL_DUPLICATE: 'BANK_MOVEMENT_PROPOSAL_DUPLICATE',
  // Raised (400) when a line whose amount or currency differs is accepted without naming a fee,
  // an FX difference or a partial payment
  BANK_MOVEMENT_DIFFERENCE_REQUIRED: 'BANK_MOVEMENT_DIFFERENCE_REQUIRED',
  // Raised (400) when a named difference is not one the line allows, its amount is not the line's
  // less the payment's, or one is named where nothing differs or on a return
  BANK_MOVEMENT_DIFFERENCE_INVALID: 'BANK_MOVEMENT_DIFFERENCE_INVALID',
  // Raised (409) when a return of more than the payment is accepted
  BANK_MOVEMENT_RETURN_EXCEEDS_PAYMENT: 'BANK_MOVEMENT_RETURN_EXCEEDS_PAYMENT',
  // Raised (400) when a partial payment is named on a line converted or in another currency than
  // the payment
  BANK_MOVEMENT_PARTIAL_CONVERTED: 'BANK_MOVEMENT_PARTIAL_CONVERTED',
  // Raised (400) when the allocation named to carry a partial payment's shortfall is no invoice
  // allocation of the payment the ERP left unconfirmed, or is named without a partial payment
  BANK_MOVEMENT_SHORTFALL_ALLOCATION_INVALID: 'BANK_MOVEMENT_SHORTFALL_ALLOCATION_INVALID',
  // Raised (409) when the allocation named to carry a partial payment's shortfall pays less than it
  BANK_MOVEMENT_SHORTFALL_EXCEEDS_ALLOCATION: 'BANK_MOVEMENT_SHORTFALL_EXCEEDS_ALLOCATION',
  // Raised (409) when a partial payment pays less than the allocations the ERP confirmed, which
  // stay paid
  BANK_MOVEMENT_PARTIAL_BELOW_CONFIRMED: 'BANK_MOVEMENT_PARTIAL_BELOW_CONFIRMED',
  // Raised (403) when whoever built or edited a run accepts or matches a confirmation by a line
  // whose revoked confirmation of that run's payment blocks its withdrawal, lifting that block
  BANK_MOVEMENT_DECIDER_IS_BLOCKED_RUN_AUTHOR: 'BANK_MOVEMENT_DECIDER_IS_BLOCKED_RUN_AUTHOR',
  // Raised (400) when a fee, an FX difference or a partial payment is named on accepting a sum of
  // several payments (kind SUM), which names none
  BANK_MOVEMENT_SUM_DIFFERENCE_INVALID: 'BANK_MOVEMENT_SUM_DIFFERENCE_INVALID',
  // Raised (409) when the payments of a sum do not add up to the statement line exactly, in its
  // currency
  BANK_MOVEMENT_SUM_AMOUNT_DIFFERS: 'BANK_MOVEMENT_SUM_AMOUNT_DIFFERS',
  // Raised (409) when a movement other than an unmatched or proposed statement line is dismissed
  BANK_MOVEMENT_NOT_DISMISSABLE: 'BANK_MOVEMENT_NOT_DISMISSABLE',
  // Raised (409) when a statement line is reopened that is not dismissed
  BANK_MOVEMENT_NOT_REOPENABLE: 'BANK_MOVEMENT_NOT_REOPENABLE',
  // Raised (400) when a statement line is dismissed without a reason
  BANK_MOVEMENT_DISMISS_REASON_REQUIRED: 'BANK_MOVEMENT_DISMISS_REASON_REQUIRED',
  // Raised (409) when a movement other than an outgoing statement line waiting in the queue is
  // matched to a payment by hand, or its candidate payments are listed
  BANK_MOVEMENT_NOT_MATCHABLE: 'BANK_MOVEMENT_NOT_MATCHABLE',
  // Raised (409) when a line is matched by hand to a payment that is no open run payment of the
  // line's own account and currency
  BANK_MOVEMENT_MATCH_NOT_CANDIDATE: 'BANK_MOVEMENT_MATCH_NOT_CANDIDATE',
  // Raised (409) when a line is matched by hand to a payment whose account is not the counterparty
  BANK_MOVEMENT_MATCH_COUNTERPARTY_DIFFERS: 'BANK_MOVEMENT_MATCH_COUNTERPARTY_DIFFERS',
  // Raised (409) when a line is matched by hand to another payment than the one its own end-to-end
  // id or structured reference names
  BANK_MOVEMENT_MATCH_NAMES_ANOTHER_PAYMENT: 'BANK_MOVEMENT_MATCH_NAMES_ANOTHER_PAYMENT',
  // Raised (409) when a line naming no counterparty is matched by hand with a difference, or to a
  // payment of another amount
  BANK_MOVEMENT_MATCH_DIFFERENCE_NEEDS_COUNTERPARTY:
    'BANK_MOVEMENT_MATCH_DIFFERENCE_NEEDS_COUNTERPARTY',
  // Raised (409) when a payment is created from a movement other than an outgoing statement line
  // waiting in the queue with no standing statement confirmation
  BANK_MOVEMENT_QUEUE_PAYMENT_LINE_INVALID: 'BANK_MOVEMENT_QUEUE_PAYMENT_LINE_INVALID',
  // Raised (409) when a payment is created from a line whose own end-to-end id or structured
  // reference names one of our payments (other than the expected rest of a partial payment)
  BANK_MOVEMENT_QUEUE_PAYMENT_NAMES_PAYMENT: 'BANK_MOVEMENT_QUEUE_PAYMENT_NAMES_PAYMENT',
  // Raised (409) when the rest of a partial payment is paid to another party than its payee
  BANK_MOVEMENT_QUEUE_PAYMENT_REST_OTHER_PAYEE: 'BANK_MOVEMENT_QUEUE_PAYMENT_REST_OTHER_PAYEE',
  // Raised (400) when a payment from the queue names no invoice, one invoice twice, or an amount
  // not above zero or with more than four decimal places
  BANK_MOVEMENT_QUEUE_PAYMENT_ALLOCATIONS_INVALID:
    'BANK_MOVEMENT_QUEUE_PAYMENT_ALLOCATIONS_INVALID',
  // Raised (400) when the amounts applied to the invoices do not add up to the line exactly
  BANK_MOVEMENT_QUEUE_PAYMENT_SUM_DIFFERS: 'BANK_MOVEMENT_QUEUE_PAYMENT_SUM_DIFFERS',
  // Raised (409) when an invoice of a payment from the queue is not the payee party's
  BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_OTHER_PARTY:
    'BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_OTHER_PARTY',
  // Raised (409) when an invoice of a payment from the queue is in another currency than the line
  BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_CURRENCY_DIFFERS:
    'BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_CURRENCY_DIFFERS',
  // Raised (409) when an invoice of a payment from the queue is archived
  BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_ARCHIVED: 'BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_ARCHIVED',
  // Raised (409) when an invoice of a payment from the queue is held by a released payment run
  BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_RESERVED: 'BANK_MOVEMENT_QUEUE_PAYMENT_INVOICE_RESERVED',
  // Raised (409) when the caller already keeps the most saved filters allowed for that list
  SAVED_LIST_FILTER_LIMIT_REACHED: 'SAVED_LIST_FILTER_LIMIT_REACHED',
  // Raised (409) when the caller already has a saved filter of that name for that list
  SAVED_LIST_FILTER_NAME_TAKEN: 'SAVED_LIST_FILTER_NAME_TAKEN'
} as const;

export type GraphQLErrorCodeType = (typeof GraphQLErrorCode)[keyof typeof GraphQLErrorCode];

/**
 * Field validation error codes
 */
export const FieldErrorCode = {
  REQUIRED: 'REQUIRED',
  INVALID_FORMAT: 'INVALID_FORMAT',
  INVALID_URL: 'INVALID_URL',
  INVALID_EMAIL: 'INVALID_EMAIL',
  INVALID_UUID: 'INVALID_UUID',
  TOO_SHORT: 'TOO_SHORT',
  TOO_LONG: 'TOO_LONG',
  MIN_VALUE: 'MIN_VALUE',
  MAX_VALUE: 'MAX_VALUE',
  INVALID_ENUM: 'INVALID_ENUM',
  INVALID_DATE: 'INVALID_DATE',
  // The connection already has a connector of the entity; `extensions.connectorName` names it
  CONNECTION_ENTITY_TAKEN: 'CONNECTION_ENTITY_TAKEN'
};

export type FieldErrorCodeType = (typeof FieldErrorCode)[keyof typeof FieldErrorCode];

/**
 * Field error structure
 */
export interface FieldError {
  field: string;
  code: string;
  message: string;
}

/**
 * GraphQL error extensions structure
 */
export interface GraphQLErrorExtensions {
  code: GraphQLErrorCodeType;
  fieldErrors?: FieldError[];
}

/**
 * Type guard to check if error extensions contain validation errors
 */
export function isValidationError(extensions: Record<string, unknown> | undefined): boolean {
  return (
    extensions?.code === GraphQLErrorCode.VALIDATION_ERROR && Array.isArray(extensions?.fieldErrors)
  );
}

/**
 * Extract field errors from GraphQL error extensions
 */
export function getFieldErrors(extensions: Record<string, unknown> | undefined): FieldError[] {
  if (extensions && isValidationError(extensions)) {
    return extensions.fieldErrors as FieldError[];
  }
  return [];
}
